import React from 'react';
import { useHistory } from 'react-router-dom';
import { Box, Button } from '@mui/material';
import { Refresh } from '@mui/icons-material';
import { colors } from 'styles/colors';
import images from 'assets/images/images';
import { PaymentResultActionsProps } from './types';
import { clarityEvent } from 'utils/hooks/useClarity';
import { getUserInfo } from 'utils/helpers';

const PaymentResultActions: React.FC<PaymentResultActionsProps> = ({
  status,
  retryPath,
  dashboardPath,
  buttonText,
  buttonIcon,
  serviceIdentifier
}) => {
  const history = useHistory();
  const userInfo = getUserInfo();

  const handleRetry = () => {
    // Track retry button click for selfcare flow
    if (userInfo.id && serviceIdentifier === 'clovemind') {
      if (status === 'failure') {
        clarityEvent("selfcare_payment_retry_clicked", {
          userId: userInfo.id
        });
      } else if (status === 'cancelled') {
        clarityEvent("selfcare_payment_cancelled_retry_clicked", {
          userId: userInfo.id
        });
      }
    }
    history.push(retryPath);
  };

  const handleGoToDashboard = () => {
    // Track dashboard button click for selfcare flow
    if (userInfo.id && serviceIdentifier === 'clovemind') {
      if (status === 'success') {
        clarityEvent("selfcare_payment_success_dashboard_clicked", {
          userId: userInfo.id
        });
      } else if (status === 'failure') {
        clarityEvent("selfcare_payment_failure_dashboard_clicked", {
          userId: userInfo.id
        });
      } else if (status === 'cancelled') {
        clarityEvent("selfcare_payment_cancelled_dashboard_clicked", {
          userId: userInfo.id
        });
      }
    }
    history.push(dashboardPath);
  };

  // Map icon identifiers to actual icon images
  const getIconForButton = (iconIdentifier?: string, variant: 'contained' | 'outlined' = 'contained') => {
    if (!iconIdentifier) {
      // Default icons based on variant
      return variant === 'contained' 
        ? <img src={images.student_home_icon} alt="Dashboard" style={{ width: 24, height: 24 }} />
        : <img src={images.home_icon_blue} alt="Dashboard" style={{ width: 24, height: 24 }} />;
    }

    // Map identifiers to actual icons
    const iconMap: { [key: string]: React.ReactNode } = {
      'home_icon': variant === 'contained'
        ? <img src={images.student_home_icon} alt="Dashboard" style={{ width: 24, height: 24 }} />
        : <img src={images.home_icon_blue} alt="Dashboard" style={{ width: 24, height: 24 }} />,
      'score_icon': variant === 'contained'
        ? <img src={images.student_score_icon_white} alt="Ma Foi Score" style={{ width: 24, height: 24 }} />
        : <img src={images.score_icon_blue} alt="Ma Foi Score" style={{ width: 24, height: 24 }} />,
      'selfcare_icon': variant === 'contained'
        ? <img src={images.selfcare_white} alt="Selfcare" style={{ width: 24, height: 24 }} />
        : <img src={images.selfcare_blue} alt="Selfcare" style={{ width: 24, height: 24 }} />
    };

    return iconMap[iconIdentifier] || iconMap['home_icon'];
  };

  const getButtonConfig = () => {
    switch (status) {
      case 'success':
        return {
          primaryButton: {
            text: buttonText || 'Go to Dashboard',
            icon: getIconForButton(buttonIcon, 'contained'),
            onClick: handleGoToDashboard,
            variant: 'contained' as const
          }
        };
      case 'failure':
        return {
          primaryButton: {
            text: 'Try Again',
            icon: <Refresh />,
            onClick: handleRetry,
            variant: 'contained' as const
          },
          secondaryButton: {
            text: buttonText || 'Go to Dashboard',
            icon: getIconForButton(buttonIcon, 'outlined'),
            onClick: handleGoToDashboard,
            variant: 'outlined' as const
          }
        };
      case 'cancelled':
        return {
          primaryButton: {
            text: 'Complete Purchase',
            icon: <Refresh />,
            onClick: handleRetry,
            variant: 'contained' as const
          },
          secondaryButton: {
            text: buttonText || 'Go to Dashboard',
            icon: getIconForButton(buttonIcon, 'outlined'),
            onClick: handleGoToDashboard,
            variant: 'outlined' as const
          }
        };
      default:
        return {
          primaryButton: {
            text: buttonText || 'Go to Dashboard',
            icon: getIconForButton(buttonIcon, 'contained'),
            onClick: handleGoToDashboard,
            variant: 'contained' as const
          }
        };
    }
  };

  const { primaryButton, secondaryButton } = getButtonConfig();

  return (
    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
      <Button
        variant={primaryButton.variant}
        size="large"
        startIcon={primaryButton.icon}
        onClick={primaryButton.onClick}
        sx={{
          backgroundColor: colors.primaryBlue,
          color: '#fff',
          borderRadius: 0,
          textTransform: 'none',
          fontWeight: 600,
          px: status === 'success' ? 6 : 4,
          py: status === 'success' ? 2 : 1.5,
          fontSize: status === 'success' ? '16px' : '14px',
          '&:hover': {
            backgroundColor: colors.primaryBlueDark
          }
        }}
      >
        {primaryButton.text}
      </Button>

      {secondaryButton && (
        <Button
          variant={secondaryButton.variant}
          size="large"
          startIcon={secondaryButton.icon}
          onClick={secondaryButton.onClick}
          sx={{
            borderColor: colors.primaryBlue,
            color: colors.primaryBlue,
            borderRadius: 0,
            textTransform: 'none',
            fontWeight: 600,
            px: 4,
            py: 1.5,
            '&:hover': {
              borderColor: colors.primaryBlueDark,
              backgroundColor: colors.primaryLight
            }
          }}
        >
          {secondaryButton.text}
        </Button>
      )}
    </Box>
  );
};

export default PaymentResultActions;